import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Box,
  Button,
  Container,
  Stack,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import HeaderBar from "../widgets/HeaderBar";
import Footer from "../widgets/Footer";
import LayoutContainer from "../widgets/LayoutContainer";

export const faqs = [
  {
    question: "What is this visitor management system?",
    answer:
      "Our system simplifies visitor registration, automates QR code generation, and provides real-time analytics.",
  },
  {
    question: "How does QR code registration work?",
    answer:
      "Visitors receive a unique QR code via email or WhatsApp, which they can scan at the entrance for quick check-in.",
  },
  {
    question: "Can I customize registration forms?",
    answer:
      "Yes, you can add or remove fields as needed, with phone number and email being mandatory for security.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Absolutely. We use encryption and industry-standard security protocols to ensure data privacy.",
  },
  {
    question: "Does the system support paid events?",
    answer:
      "Yes, you can set up paid registrations, and the system automatically validates QR codes for entry.",
  },
];
export const FAQAccordion = ({ question, answer }) => (
  <Accordion
    sx={{
      boxShadow: 3,
      borderRadius: 2,
      transition: "all 0.3s ease-in-out",
      background: "linear-gradient(135deg, #ffffff, #f9f9f9)",
      "&:hover": {
        boxShadow: 6,
        background: "linear-gradient(135deg, #f9f9f9, #eaeaea)",
      },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="h6" fontWeight="bold">
        {question}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Typography variant="body2">{answer}</Typography>
    </AccordionDetails>
  </Accordion>
);

const FaqsPage = () => {
  const theme = useTheme();

  return (
    <LayoutContainer>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 6,
            color: theme.palette.text.primary,
            [theme.breakpoints.down("sm")]: { fontSize: "2.5rem" },
          }}
        >
          Frequently Asked Questions
        </Typography>

        <Stack spacing={2} sx={{ mb: 8 }}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                borderRadius: "8px !important",
                boxShadow: theme.shadows[2],
                transition: "all 0.2s ease-in-out",
                "&:before": { display: "none" },
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  transform: "translateY(-2px)",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="primary" />}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "8px",
                  "&.Mui-expanded": {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  bgcolor: theme.palette.background.default,
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>

        {/* Contact Section */}
        <Box
          sx={{
            textAlign: "center",
            p: 6,
            borderRadius: 4,
            bgcolor: theme.palette.primary.light,
            backgroundImage:
              "linear-gradient(to bottom right, rgba(255,255,255,0.1), transparent)",
            boxShadow: theme.shadows[4],
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              bgcolor: theme.palette.primary.main,
              borderRadius: "50%",
              opacity: 0.1,
            },
          }}
        >
          <ContactSupportIcon
            sx={{
              fontSize: 64,
              color: theme.palette.primary.main,
              mb: 3,
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
            }}
          />

          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            Still have questions?
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              maxWidth: 500,
              mx: "auto",
            }}
          >
            Can't find the answer you're looking for? Our support team is here
            to help!
          </Typography>

          <Button
            variant="contained"
            size="large"
            endIcon={<ContactSupportIcon />}
            href="/contactus" // Update with your contact route
            sx={{
              px: 6,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: theme.shadows[4],
              "&:hover": {
                boxShadow: theme.shadows[8],
                transform: "translateY(-2px)",
              },
            }}
          >
            Contact Support Team
          </Button>
        </Box>
      </Container>
    </LayoutContainer>
  );
};

export default FaqsPage;
